import * as React from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import strings from './../utils/StringManager';
import { IGroupData, useGetAllGroupsApi, useGetVisiblesGroupsApi } from '../services/useGroupsApi';
import { Badge, Grid, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

interface IGroupNamesAutocompleteProps {
    value: string[],
    onChange: (value: string[]) => void,

    disabled?: boolean,
    fullWidth?: boolean,
    label: string,
    className?: string,
}

export function GroupNamesAutocomplete(props: IGroupNamesAutocompleteProps) {

    const [setModel, result, isLoading, isError] = useGetAllGroupsApi();
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<IGroupData[]>([]);
    const [init, setInit] = React.useState(false);

    const [value, setValue] = React.useState<string[]>(props.value);

    React.useEffect(() => {
        if (!open || init) {
            return undefined;
        }
        setInit(true);
        setModel({
            _: new Date(),
        } as any)

    }, [open]);

    React.useEffect(() => {
        if (result) {
            setOptions(result);
        }
    }, [result]);

    React.useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    return (
        <Autocomplete
            multiple
            className={clsx("mi-lab-autocomplete-outlined", props.className)}
            value={value?.map(valueMapper)}
            onChange={(event, newValues) => {
                let values = newValues.map(newValue => {
                    if (typeof newValue === 'string')
                        return newValue;
                    else
                        return newValue.Name;
                })
                props.onChange(values);
            }}
            fullWidth={props.fullWidth}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionSelected={(option, value) => option.Name === value.Name}
            getOptionLabel={option => option.Name}
            options={options}
            loading={isLoading}
            disabled={props.disabled}

            clearText={strings.get("clear")}
            noOptionsText={strings.get("noOptionsMessage")}
            openText={strings.get("open")}
            closeText={strings.get("close")}
            loadingText={strings.get("loading") + "..."}

            renderInput={params => (
                <TextField
                    {...params}
                    label={props.label}
                    variant="outlined"
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'disabled',
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}

            renderOption={(option) => {
                return <div>
                    {option.Name}
                    <Typography variant="body2" color="textSecondary">
                        {option.Description}
                    </Typography>
                </div>
            }}
        />
    );
}

interface INameProps {
    value: string,
    onChange: (value: string) => void,

    disabled?: boolean,
    fullWidth?: boolean,
}

export function GroupNameAutocomplete(props: INameProps) {

    const [setModel, result, isLoading, isError] = useGetAllGroupsApi();
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<IGroupData[]>([]);
    const [init, setInit] = React.useState(false);

    const [value, setValue] = React.useState<string>(props.value);

    React.useEffect(() => {
        if (!open || init) {
            return undefined;
        }
        setInit(true);
        setModel({
            _: new Date(),
        } as any)

    }, [open]);

    React.useEffect(() => {
        if (result) {
            setOptions(result);
        }
    }, [result]);

    React.useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    return (
        <Autocomplete
            className="mi-lab-autocomplete-outlined"
            value={valueMapper(value)}
            onChange={(event, newValue) => {
                if (typeof newValue === 'string')
                    props.onChange(newValue);
                else
                    props.onChange(newValue.Name);
            }}
            fullWidth={props.fullWidth}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionSelected={(option, value) => option.Name === value.Name}
            getOptionLabel={option => option.Name}
            options={options}
            loading={isLoading}
            disabled={props.disabled}

            clearText={strings.get("clear")}
            noOptionsText={strings.get("noOptionsMessage")}
            openText={strings.get("open")}
            closeText={strings.get("close")}
            loadingText={strings.get("loading") + "..."}

            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'disabled',
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}

            renderOption={(option) => {
                return <div>
                    {option.Name}
                    <Typography variant="body2" color="textSecondary">
                        {option.Description}
                    </Typography>
                </div>
            }}
        />
    );
}

export interface OptionType {
    value: string;
    label: string;
}

function valueMapper(item: OptionType | string): IGroupData {

    if (!item)
        return null;

    if (typeof item === 'string')
        return {
            Id: item,
            Name: item,

            Description: "",
            AddedBySystem: false,
            System: false,

            ExecutorInIssuesCounter: 0,
            AssignedInIssuesCounter: 0,
            CoordinatorInIssuesCounter: 0,
        };

    return {
        Id: item.value,
        Name: item.label,

        Description: "",
        AddedBySystem: false,
        System: false,

        ExecutorInIssuesCounter: 0,
        AssignedInIssuesCounter: 0,
        CoordinatorInIssuesCounter: 0,
    };
}

function dataMapper(item: IGroupData | string): OptionType {

    if (!item)
        return null;

    if (typeof item === "string") {
        return {
            label: item,
            value: item
        };
    }
    else {
        return {
            label: item.Name,
            value: item.Id
        };
    }
}

interface IProps {
    value: OptionType,
    onChange: (value: OptionType) => void,

    className?: string,

    disabled?: boolean,
    fullWidth?: boolean,
    errorText?: string,

    filter?: string,
    counter?: string,
}

export default function GroupsAutocomplete(props: IProps) {

    const { t } = useTranslation();

    const [setModel, result, isLoading, isError] = useGetVisiblesGroupsApi();
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<IGroupData[]>([]);
    const [init, setInit] = React.useState(false);

    const [value, setValue] = React.useState<IGroupData>(() => valueMapper(props.value));
   
    React.useEffect(() => {
        if (!open || init) {
            return undefined;
        }
        setInit(true);
        setModel({
            filter: props.filter,
        })

    }, [open]);

    React.useEffect(() => {
        if (result) {
            setOptions(result);
        }
    }, [result]);

    React.useEffect(() => {
        setValue(valueMapper(props.value));
    }, [props.value]);

    return (<Autocomplete
            className={clsx("mi-lab-autocomplete-outlined", props.className)}
            value={value}
            onChange={(event, newValue) => {
                props.onChange(dataMapper(newValue));
            }}
            fullWidth={props.fullWidth}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionSelected={(option, value) => option.Id === value.Id}
            getOptionLabel={option => option.Name}
            options={options}
            loading={isLoading}
            disabled={props.disabled}

            clearText={t("common.action.clear")}
            noOptionsText={t("control.autocomplete.noOptionsMessage")}
            openText={t("common.action.open")}
            closeText={t("common.action.close")}
            loadingText={t("common.loading") + "..."}

            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    helperText={props.errorText}
                    error={!!props.errorText}
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'disabled',
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}

            renderOption={(option) => {
                let counter: number = option[props.counter];
                return (<Grid container alignItems="center">
                    <Grid item xs>
                        {option.Name}
                        <Typography variant="body2" color="textSecondary">
                            {option.Description}
                        </Typography>
                    </Grid>
                    {!!counter && <Grid item>
                        <Badge
                            badgeContent={counter}
                            title={t("control.autocomplete.group." + props.counter, { counter: counter })}
                        />
                    </Grid>}
                </Grid>)
            }}
        />);
}
